import { useCallback, useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { mutateData } from "@/core/api/api-methods";
import { MAX_PAGE_SIZE, pageCount, type Paged } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import { useWallets } from "@/features/wallet/api/use-wallets";
import type { WalletDto, WalletFilter } from "@/features/wallet/api/dto";
import {
  WalletFormDialog,
  walletsColumns,
  type WalletRow,
} from "@/features/wallet/components";

/** Traduit les filtres de colonnes DataTableClar en `WalletFilter` (Postman `POST /Wallets/all`). */
const toWalletFilter = (filters: ColumnFiltersState): WalletFilter => {
  const filter: WalletFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "ownerPartyId") filter.ownerPartyId = value;
    if (id === "ownerType") filter.ownerType = value as WalletFilter["ownerType"];
    if (id === "status") filter.status = value as WalletFilter["status"];
  }
  return filter;
};

/**
 * Liste paginée des portefeuilles — file de travail back-office
 * (`POST /Wallets/all`). Filtres sur `ownerPartyId` (texte), `ownerType`
 * et `status` (select) — champs du `WalletFilter` Postman.
 */
export function WalletsPage() {
  const { t } = useTypedTranslation();
  // Les clés wallet.wallets.* vivent dans features/wallet/i18n/wallets.json
  // en attendant leur fusion dans src/locales par le coordinateur.
  const tWallet = (key: string) => t(key as TFunctionType);
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toWalletFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useWallets(params);

  // Filtre client-side sur `ownerName` : kyc-service n'indexe que téléphone
  // et email (Postman v3.1 — « Le filtrage par nom n'est pas assuré côté
  // serveur »). On applique donc le filtre uniquement sur la page affichée.
  const ownerNameQuery = useMemo(() => {
    const raw = columnFilters.find((f) => f.id === "ownerName")?.value;
    return typeof raw === "string" ? raw.trim().toLowerCase() : "";
  }, [columnFilters]);

  const rows = useMemo(() => {
    const items = (data?.items ?? []).filter(
      (w): w is WalletRow => Boolean(w.id)
    );
    if (!ownerNameQuery) return items;
    return items.filter((w) =>
      (w.ownerName ?? "").toLowerCase().includes(ownerNameQuery)
    );
  }, [data, ownerNameQuery]);

  // Export complet : boucle sur `POST /Wallets/all` à `MAX_PAGE_SIZE` (200,
  // guide §4.5) jusqu'à épuiser le `total`. Les filtres serveur actifs
  // (ownerPartyId, ownerType, status) sont réappliqués via `params.filter` ;
  // le filtre client-side ownerName est également appliqué sur l'ensemble.
  const fetchAllWalletsForExport = useCallback(async (): Promise<
    WalletRow[]
  > => {
    const collected: WalletDto[] = [];
    let page = 1;
    let total = Infinity;
    while (collected.length < total) {
      const paged = (await mutateData({
        url: walletEndpoints.wallets.all,
        reqBody: {
          filter: params.filter,
          page,
          pageSize: MAX_PAGE_SIZE,
        } as unknown as Record<string, unknown>,
        method: "POST",
      })) as Paged<WalletDto>;
      total = paged.total;
      collected.push(...paged.items);
      if (!paged.items.length) break;
      page += 1;
    }
    const typed = collected.filter((w): w is WalletRow => Boolean(w.id));
    if (!ownerNameQuery) return typed;
    return typed.filter((w) =>
      (w.ownerName ?? "").toLowerCase().includes(ownerNameQuery)
    );
  }, [params.filter, ownerNameQuery]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {tWallet("wallet.wallets.title")}
        </h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tWallet("wallet.wallets.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={walletsColumns}
        title={tWallet("wallet.wallets.title")}
        exportFileName="wallets"
        fetchAllForExport={fetchAllWalletsForExport}
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <WalletFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
